// src/components/course_resources/StudentResourcesPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./course_resources.css";

const COURSE_LABELS = {
  nanoblading_hairstrokes: "Nano",
  ombre_powder_brows: "Ombré",
  lip_blush: "Lips",
};

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

// ✅ Normalize to UTC midnight so dates don’t show “one day before” on some devices
const toUTCDateOnly = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
};

const formatShortDateUTC = (d) => {
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getOpenUrl = (r) => (r?.file?.url || r?.url || "").trim();

const isImageResource = (r) => {
  const mime = String(r?.file?.fileType || "").toLowerCase();
  const href = String(getOpenUrl(r) || "").toLowerCase();
  const ext = href.split("?")[0].split("#")[0].split(".").pop() || "";
  if (String(r?.resourceType || "").toLowerCase() === "image_url") return true;
  if (mime.startsWith("image/")) return true;
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return true;
  return false;
};

const getKind = (r) => {
  const t = String(r?.resourceType || "").toLowerCase();
  const mime = String(r?.file?.fileType || "").toLowerCase();
  const href = String(getOpenUrl(r) || "").toLowerCase();
  const ext = href.split("?")[0].split("#")[0].split(".").pop() || "";

  if (t === "video" || href.includes("youtube") || href.includes("vimeo")) return "video";
  if (t === "pdf_url" || mime.includes("pdf") || ext === "pdf") return "pdf";
  if (mime.includes("word") || ["doc", "docx"].includes(ext)) return "docx";
  if (isImageResource(r)) return "image";
  return "link";
};

const IconTile = ({ kind }) => {
  const label =
    kind === "pdf"
      ? "PDF"
      : kind === "image"
      ? "IMG"
      : kind === "docx"
      ? "DOC"
      : kind === "video"
      ? "VID"
      : "LINK";

  return <div className={`cr-thumb cr-thumb-${kind}`}>{label}</div>;
};

export default function StudentResourcesPanel({
  enrolledCourseKeys = [],
  resourcesByCourseKey = {}, // { courseKey: [resourceObj...] }
  courseStartDatesByKey = {}, // { courseKey: dateStringOrDate }
  isLoading = false,
  error = "",
  fetchCourseResources,
}) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchCourseResources && fetchCourseResources({ includeInactive: false });
  }, [fetchCourseResources]);

  /**
   * ✅ Outputs:
   * - visibleResources: only from unlocked courses (startDate <= today) OR courses with no startDate
   * - nextUnlockLabel: earliest future startDate among courses that actually have resources
   * - lockedCourses: list of enrolled courseKeys that are still locked (future start date)
   * - hasAnyResourcesAcrossEnrollments: whether ANY enrolled course has resources at all
   */
  const {
    visibleResources,
    nextUnlockLabel,
    lockedCourses,
    hasAnyResourcesAcrossEnrollments,
  } = useMemo(() => {
    const keys = uniq(enrolledCourseKeys);
    const now = toUTCDateOnly(new Date());

    const locked = [];
    const merged = [];

    // Do ANY enrolled courses have resources at all?
    const hasAny = keys.some((k) => (resourcesByCourseKey?.[k] || []).length > 0);

    // Determine which courses are locked vs unlocked and collect visible resources
    keys.forEach((k) => {
      const startDate = toUTCDateOnly(courseStartDatesByKey?.[k]);
      const unlocked = !startDate || (now && startDate && now >= startDate);

      if (!unlocked) locked.push(k);

      if (!unlocked) return;

      (resourcesByCourseKey?.[k] || []).forEach((r) => {
        if (!r?._id) return;
        if (!r?.isActive) return;
        merged.push(r);
      });
    });

    // Next unlock date = earliest future start date among LOCKED courses that have resources
    const lockedStartDatesWithResources = locked
      .filter((k) => (resourcesByCourseKey?.[k] || []).length > 0)
      .map((k) => toUTCDateOnly(courseStartDatesByKey?.[k]))
      .filter(Boolean)
      .filter((d) => now && d > now);

    const earliestLocked =
      lockedStartDatesWithResources.length > 0
        ? new Date(Math.min(...lockedStartDatesWithResources.map((d) => d.getTime())))
        : null;

    const nextLabel = earliestLocked ? formatShortDateUTC(earliestLocked) : "";

    // de-dupe by id (resources can belong to multiple courses)
    const byId = {};
    merged.forEach((r) => {
      byId[r._id] = r;
    });

    const arr = Object.values(byId);

    // sort by sortOrder asc, then createdAt desc
    arr.sort((a, b) => {
      const aS = typeof a?.sortOrder === "number" ? a.sortOrder : 50;
      const bS = typeof b?.sortOrder === "number" ? b.sortOrder : 50;
      if (aS !== bS) return aS - bS;

      const aT = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bT = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bT - aT;
    });

    return {
      visibleResources: arr,
      nextUnlockLabel: nextLabel,
      lockedCourses: locked,
      hasAnyResourcesAcrossEnrollments: hasAny,
    };
  }, [enrolledCourseKeys, resourcesByCourseKey, courseStartDatesByKey]);

  if (!enrolledCourseKeys.length) return null;

  return (
    <section className="cr-card cr-student">
      {/* Collapsible header */}
      <button type="button" className="dash-accordion-head" onClick={() => setIsOpen((v) => !v)}>
        <div className="dash-accordion-title">
          <h2 className="cr-title" style={{ margin: 0 }}>
            Your Course Resources
          </h2>

          <p className="cr-sub" style={{ margin: "6px 0 0" }}>
            Downloads + links for your enrolled courses.
          </p>
        </div>

        <span className={`dash-accordion-chevron ${isOpen ? "is-open" : ""}`} aria-hidden="true">
          ▾
        </span>
      </button>

      {isOpen && (
        <div className="dash-accordion-body">
          {isLoading ? <div className="cr-muted">Loading…</div> : null}
          {error ? <div className="cr-error">{error}</div> : null}

          {/* ✅ Banner ONLY when some are visible AND more are coming later */}
          {!isLoading && visibleResources.length > 0 && nextUnlockLabel ? (
            <div className="cr-empty cr-empty-unlock" style={{ marginBottom: 10 }}>
              More resources unlock on{" "}
              <span style={{ fontWeight: 700 }}>{nextUnlockLabel}</span>.
            </div>
          ) : null}

          {/* ✅ Empty states */}
          {!isLoading && visibleResources.length === 0 ? (
            // show unlock message ONLY if there are resources somewhere but all are locked
            hasAnyResourcesAcrossEnrollments && nextUnlockLabel ? (
              <div className="cr-empty cr-empty-unlock">
                Resources unlock on{" "}
                <span style={{ fontWeight: 700 }}>{nextUnlockLabel}</span>.
              </div>
            ) : (
              <div className="cr-empty">No resources yet for your courses.</div>
            )
          ) : null}

          <div className="cr-grid">
            {visibleResources.map((r) => {
              const href = getOpenUrl(r);
              const kind = getKind(r);
              const isImg = isImageResource(r);

              // optional: keep labels available if you ever want to show them again
              // const courseLabels = uniq(r?.courseKeys || [])
              //   .map((k) => COURSE_LABELS[k] || k)
              //   .filter(Boolean);

              return (
                <a
                  key={r._id}
                  className="cr-row"
                  href={href || "#"}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    if (!href) e.preventDefault();
                  }}
                  title={href || "Missing URL"}
                >
                  {/* thumbnail / icon */}
                  {isImg && href ? (
                    <div className="cr-thumb cr-thumb-image">
                      <img src={href} alt="" loading="lazy" />
                    </div>
                  ) : (
                    <IconTile kind={kind} />
                  )}

                  {/* text */}
                  <div className="cr-row-main">
                    <div className="cr-row-title">{r.title || "Untitled resource"}</div>
                    {r.description ? <div className="cr-row-desc">{r.description}</div> : null}
                  </div>

                  <div className={`cr-open ${href ? "" : "disabled"}`}>{href ? "Open" : "Missing"}</div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
